import {
  add,
  differenceInDays,
  isSameDay,
  isSameWeek,
  isSameYear,
  isValid,
  parse,
  format,
  isPast,
  isWithinInterval
} from "date-fns";
import { captureMessage } from "@sentry/react";
import { nl, enGB } from "date-fns/locale";
import { DATE_FNS_FORMAT, NO_END_DATE } from "./constants";
import { TLang } from "../assets/data/isoNameLanguage";

const SERVER_DATE_FNS_FORMAT = "yyyy-MM-dd"; // e.g. "2025-01-31"

export const parseServerDate = (date: string): Date => {
  return parse(date, SERVER_DATE_FNS_FORMAT, new Date(2000, 0, 1, 0, 0, 0, 0));
};

export const formatToServerDate = (date: Date): string => {
  return format(date, SERVER_DATE_FNS_FORMAT);
};

export const parseServerDateTime = (string: string): Date => {
  return new Date(string + "Z");
};

export const parseServerDateOrTime = (string: string): Date => {
  const date = parseServerDate(string);
  if (isValid(date)) {
    return date;
  } else {
    const dateWithTime = new Date(string + "Z");
    if (isValid(dateWithTime)) {
      return dateWithTime;
    } else {
      throw Error(`Date string is not valid: ${string}`);
    }
  }
};
export const formatToServerDateTime = (date: Date): string => {
  return date.toISOString().replace("Z", "000");
};
export const formatAsUTCToServerDateTime = (date: Date): string => {
  return formatToServerDateTime(
    add(date, { minutes: -date.getTimezoneOffset() })
  );
};
export const formatAsUTCToServerDate = (date: Date): string => {
  return formatToServerDate(add(date, { minutes: -date.getTimezoneOffset() }));
};

export const formatDateToString = (
  date: Date,
  dateFormat: string | undefined = DATE_FNS_FORMAT,
  lng?: string
): string => {
  if (!isValid(date)) {
    captureMessage("Date is not valid");
    return "Invalid date";
  }
  return format(date, dateFormat, {
    locale: lng === "nl" ? nl : enGB
  });
};

export const formatDateTime = (
  date: Date,
  minimizeOutputLength: boolean | undefined = true,
  lng?: string
): string => {
  const locale = lng === "nl" ? nl : enGB;
  if (!minimizeOutputLength) {
    return format(date, "dd MMM yyyy HH:mm", { locale });
  }

  if (isSameDay(new Date(), date)) {
    return format(date, "HH:mm");
  } else if (isSameWeek(new Date(), date)) {
    return format(date, "EEEE HH:mm", { locale });
  } else if (!isSameWeek(new Date(), date)) {
    if (isSameYear(new Date(), date)) {
      return format(date, "dd MMM HH:mm", { locale });
    }
    return format(date, "dd MMM yyyy HH:mm", { locale });
  }

  return format(date, "dd MMM yyyy HH:mm", { locale });
};

export const noEndDate = (endDate: Date) => {
  const noEndDateDaysDif = differenceInDays(NO_END_DATE, endDate);

  return noEndDateDaysDif >= -1 && noEndDateDaysDif <= 1;
};

export const dateString = (
  value: string | undefined,
  curLang: TLang,
  isShortMonth?: boolean
): string => {
  if (!value || value === "") return "";
  const dt = new Date(value);

  return `${format(dt, `d MMM${isShortMonth ? "" : "M"}`, {
    locale: curLang === "NL" ? nl : enGB
  })} '${dt.getFullYear().toString().slice(-2)}`;
};

export const isAvailableNow = (
  start?: string,
  end?: string | null
): boolean => {
  if (!start || typeof end === "undefined") return false;
  const startDate = parseServerDateOrTime(start);

  if (isValid(startDate)) {
    if (end === null || end === "") {
      return isPast(startDate);
    } else {
      const endDate = parseServerDateOrTime(end);

      return isValid(endDate)
        ? isWithinInterval(new Date(), {
            start: startDate,
            end: endDate
          })
        : false;
    }
  } else return false;
};

export const sortByCreationTime = (
  a: { created_at: string },
  b: { created_at: string },
  isAscending?: true
) => {
  let aDate = new Date(a.created_at).getTime(),
    bDate = new Date(b.created_at).getTime();
  if (aDate < bDate) return isAscending ? 1 : -1;
  if (aDate > bDate) return isAscending ? -1 : 1;
  return 0;
};

export const isToday = (date: Date) =>
  format(date, "yyyy/LL/dd") === format(new Date(), "yyyy/LL/dd");

export const convertUTCToLocalDate = (date: Date) => {
  const browserTimeZoneOffsetMs = -60000 * new Date().getTimezoneOffset();
  return new Date(date.getTime() + browserTimeZoneOffsetMs);
};
